import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output } from '@angular/core';
import { BaseComponent } from 'src/app/core/base.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { LeagueIterationService } from '../services/league-iteration.service';
import { LeagueIteration } from '../models/league-iteration';
import { League } from '../models/league';
import { MatDialog } from '@angular/material/dialog';
import { provideNativeDateAdapter } from '@angular/material/core';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { CreateLeagueIterationModalComponent } from '../modals/league-iteration/create-league-iteration-modal/create-league-iteration-modal.component';
import { EditLeagueIterationModalComponent } from '../modals/league-iteration/edit-league-iteration-modal/edit-league-iteration-modal.component';
import { CloneLeagueIterationModalComponent } from '../modals/league-iteration/clone-league-iteration-modal/clone-league-iteration-modal.component';
import { LeagueIterationMembersListComponent } from '../league-iteration-members-list/league-iteration-members-list.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment-timezone';
import { LeagueIterationsListQueryFilter } from './league-iterations-list-query-filter';
import { LeagueEventEmitters } from '../booking-extensions/services/league-event-emitters';

@Component({
    selector: 'league-iterations-list',
    templateUrl: './league-iterations-list.component.html',
    styleUrl: './league-iterations-list.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class LeagueIterationsListComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() league: League | null = null;
    @Output() iterationsUpdated = new EventEmitter<void>();
    @Output() onCloseIterationsPanel = new EventEmitter<void>();

    public iterations: LeagueIteration[] = [];
    public displayedColumns: string[] = ['year', 'startDate', 'endDate', 'startTime', 'endTime', 'entryFee', 'actions'];
    public userTimezone: string;
    public filter: LeagueIterationsListQueryFilter = new LeagueIterationsListQueryFilter();
    public updateFilterEvent = new EventEmitter<any>();
    public selectedIteration: LeagueIteration | null = null;

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: League | null,
        @Optional() public dialogRef: MatDialogRef<LeagueIterationsListComponent>,
        sessionService: SessionService,
        stringHelpers: StringHelpers,
        private dialog: MatDialog,
        private leagueIterationService: LeagueIterationService,
        private leagueEventEmitters: LeagueEventEmitters
    ) {
        super(sessionService, stringHelpers);
        if (this.data && !this.league) {
            this.league = this.data;
        }
        this.filter.pageSize = 10;
    }

    ngOnInit(): void {
        this.userTimezone = this.sessionService.getUser().timeZone;
        if (this.league && this.league.id) {
            this.filter.leagueId = this.league.id;
            this.getIterations();
        }

        this.leagueEventEmitters.leagueChangedEvent.subscribe(() => {
            this.selectedIteration = null;
        });
    }

    ngOnChanges(): void {
        if (this.league && this.league.id) {
            this.filter.leagueId = this.league.id;
            this.getIterations();
        }
    }

    getIterations(): void {
        if (!this.league || !this.league.id) {
            return;
        }

        const selectedIterationId = this.selectedIteration?.id;

        this.isLoading = true;
        this.leagueIterationService.getAllByLeagueId(this.filter).subscribe({
            next: (result: any) => {
                this.iterations = result.results || [];
                this.filter.totalCount = result.totalCount;
                this.updateFilterEvent.emit(result.results);

                if (selectedIterationId) {
                    const updatedIteration = this.iterations.find((iter: LeagueIteration) => iter.id === selectedIterationId);
                    if (updatedIteration) {
                        this.selectedIteration = updatedIteration;
                    }
                }

                this.isLoading = false;
            },
            error: (error: any) => {
                this.isLoading = false;
                console.error(error);
                this.alert(error);
            }
        });
    }

    onCreate(): void {
        const dialogRef = this.dialog.open(CreateLeagueIterationModalComponent, {
            maxWidth: '90vw',
            maxHeight: '90vh',
            disableClose: true,
            data: this.league
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getIterations();
                this.notifyParent();
            }
        });
    }

    onEdit(iteration: LeagueIteration): void {
        const dialogRef = this.dialog.open(EditLeagueIterationModalComponent, {
            maxWidth: '90vw',
            maxHeight: '95vh',
            disableClose: true,
        });

        dialogRef.componentInstance.iteration = iteration;

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getIterations();
                this.notifyParent();
            }
        });
    }

    onClone(iteration: LeagueIteration): void {
        const dialogRef = this.dialog.open(CloneLeagueIterationModalComponent, {
            maxWidth: '95vw',
            disableClose: true,
            data: iteration
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getIterations();
                this.notifyParent();
            }
        });
    }

    onManageMembers(iteration: LeagueIteration): void {
        const dialogRef = this.dialog.open(LeagueIterationMembersListComponent, {
            width: '75vw',
            height: '65vh',
            data: iteration
        });

        dialogRef.afterClosed().subscribe(() => {
            this.getIterations();
        });
    }

    onDelete(iteration: LeagueIteration): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Delete Iteration';
        dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> 
        Are you sure you want to delete iteration <strong>${iteration.year}</strong> for ${this.league.name}? 
        <br /><br />All member registrations and future bookings for this iteration will be deleted.`;

        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                this.isLoading = true;
                this.leagueIterationService.delete({
                    id: iteration.id,
                    lastEditedBy: this.sessionService.getUserId(),
                    organizationId: this.sessionService.getUser().organizationId,
                }).subscribe({
                    next: (result) => {
                        this.getIterations();
                        this.isLoading = false;
                        this.notifyParent();
                    },
                    error: (error) => {
                        this.alert(error);
                        console.error(error);
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    notifyParent(): void {
        this.iterationsUpdated.emit();
    }

    formatInUserTimezone(date: Date, format: string): string {
        return moment.utc(date).tz(this.userTimezone).format(format);
    }

    alert(error: any): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        let errorMessage = '<h3 class="mat-h3">You have following errors:</h3><ul> ';

        if (error.status === 400) {
            error.error.forEach((error: any) => {
                errorMessage += `<li>${error.errorMessage}</li>`;
            });
        } else {
            errorMessage += `<li>There was an error loading the iterations, Please try again or contact support if the problem persists</li>`;
        }

        dialogRef.componentInstance.title = 'Iterations Error';
        dialogRef.componentInstance.body = errorMessage + '</ul>';
        dialogRef.componentInstance.submitText = 'OK';
    }
}

